import React, { useState, useRef, useEffect } from "react";
import { FiUpload, FiX } from "react-icons/fi";
import { toast } from "react-hot-toast";
import { v4 as uuidv4 } from "uuid";
import uploadImage from "../../utils/CloudinaryUpload";
import { uploadVideoToCloudinary } from "../../utils/cloudinary";
import axiosInstance from "../../utils/axios";
import { useNavigate } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";

const AdminUploadGallery = () => {
  const [items, setItems] = useState([]); // unified list for image/video/media files { id, file, preview, title, sequence, uploading, url, category }
  const [uploadingAll, setUploadingAll] = useState(false);
  const [activeTab, setActiveTab] = useState("image"); // image | video | media
  const fileInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const mediaInputRef = useRef(null);
  const navigate = useNavigate();

  // Helpers
  const resetInputs = () => {
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (videoInputRef.current) videoInputRef.current.value = "";
    if (mediaInputRef.current) mediaInputRef.current.value = "";
  };

  // Add selected files to items array (category is set based on tab)
  const addFiles = (files, category) => {
    if (!files || files.length === 0) return;
    const newFiles = Array.from(files).map((file, idx) => ({
      id: uuidv4(),
      file,
      preview: URL.createObjectURL(file),
      title: "",
      sequence: items.length + idx + 1, // default sequence appended
      uploading: false,
      url: null,
      category,
    }));
    setItems((prev) => [...prev, ...newFiles]);
    resetInputs();
  };

  // Handlers for file inputs
  const handleImageChange = (e) => {
    addFiles(e.target.files, "image");
  };

  const handleVideoChange = (e) => {
    addFiles(e.target.files, "video");
  };

  const handleMediaChange = (e) => {
    // media are treated like images for upload
    addFiles(e.target.files, "media");
  };

  // When activeTab changes, update the category of items that are not uploaded yet.
  // This fixes the bug where changing tab didn't update category for already selected files.
  useEffect(() => {
    setItems((prev) =>
      prev.map((it) => (it.url ? it : { ...it, category: activeTab }))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Remove an item
  const handleRemoveItem = (id) => {
    const it = items.find((i) => i.id === id);
    if (it && it.preview) URL.revokeObjectURL(it.preview);
    setItems((prev) => prev.filter((i) => i.id !== id));
    toast.success("Removed");
  };

  // Update title or sequence per item
  const handleItemFieldChange = (id, field, value) => {
    setItems((prev) =>
      prev.map((it) =>
        it.id === id
          ? {
              ...it,
              [field]: field === "sequence" ? Number(value || 0) : value,
            }
          : it
      )
    );
  };

  const uploadSingleItem = async (item) => {
    // Skip if already uploaded (url present)
    if (item.url) return { ...item };

    const copy = { ...item, uploading: true };
    setItems((prev) => prev.map((i) => (i.id === item.id ? copy : i)));

    try {
      let uploadedUrl = null;
      if (item.category === "video") {
        // If uploadVideoToCloudinary supports progress callback, you can pass one here.
        uploadedUrl = await uploadVideoToCloudinary(item.file);
      } else {
        // image or media
        uploadedUrl = await uploadImage(item.file);
      }

      const done = { ...copy, uploading: false, url: uploadedUrl };
      setItems((prev) => prev.map((i) => (i.id === item.id ? done : i)));
      return done;
    } catch (err) {
      console.error("Upload failed for item:", item, err);
      const failed = { ...copy, uploading: false };
      setItems((prev) => prev.map((i) => (i.id === item.id ? failed : i)));
      throw err;
    }
  };

  // Upload all (parallel with Promise.all)
  const handleUploadAll = async () => {
    if (items.length === 0) {
      toast.error("No files to upload");
      return;
    }

    setUploadingAll(true);

    try {
      const itemsToUpload = items.filter((it) => !it.url);

      // If everything is already uploaded
      if (itemsToUpload.length === 0) {
        toast.success("All files already uploaded");
        setUploadingAll(false);
        return;
      }

      // Run uploads in parallel
      await Promise.all(itemsToUpload.map((item) => uploadSingleItem(item)));

      toast.success("All files uploaded successfully!");
    } catch (err) {
      console.error("Upload error:", err);
      toast.error("Some uploads failed. Please retry.");
    } finally {
      setUploadingAll(false);
    }
  };

  // Save all to backend (bulk)
  const handleSaveAll = async () => {
    // prepare payload only for uploaded files
    const payloadItems = items
      .filter((it) => it.url) // ensure uploaded
      .map((it) => ({
        title: it.title || "",
        category: it.category,
        fileUrl: it.url,
        sequence: typeof it.sequence === "number" ? it.sequence : 0,
      }));

    if (payloadItems.length === 0) {
      toast.error("No uploaded files to save. Upload first.");
      return;
    }

    try {
      await axiosInstance.post("/gallery/bulk", { items: payloadItems });
      toast.success("Gallery saved successfully");
      // cleanup previews
      items.forEach((it) => it.preview && URL.revokeObjectURL(it.preview));
      setItems([]);
    } catch (err) {
      console.error("Save bulk failed", err);
      toast.error("Save failed");
    }
  };

  // Determine if all items are uploaded
  const allUploaded = items.length > 0 && items.every((it) => it.url);

  // UI: keep design identical; only add fields under each preview: title, sequence
  return (
    <div className="p-6 max-w-full mx-auto relative min-h-screen">
      {/* Header */}
      <div className="w-full flex justify-between items-center mb-8">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center justify-center w-12 h-12 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors duration-200 group"
        >
          <FaArrowLeft className="text-gray-600 group-hover:text-gray-800 transition-colors duration-200" />
        </button>
        <div className="grow flex items-center justify-start px-4">
          <h1 className="text-3xl text-left font-bold">
            {activeTab === "video"
              ? "Upload Videos"
              : activeTab === "media"
              ? "Upload Media Publications"
              : "Upload one or multiple images"}
          </h1>
        </div>
        <div>
          {items.length > 0 && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  // revoke previews
                  items.forEach(
                    (it) => it.preview && URL.revokeObjectURL(it.preview)
                  );
                  setItems([]);
                  resetInputs();
                }}
                className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white font-semibold rounded-lg shadow-md transition-all duration-200"
              >
                Clear
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex justify-start mt-8 mb-6 border-b border-gray-300">
        {[
          { key: "image", label: "Images" },
          { key: "video", label: "Videos" },
          { key: "media", label: "Media Files" },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`relative px-6 py-3 mx-2 text-sm md:text-base font-semibold transition-all duration-300 ${
              activeTab === tab.key
                ? "text-[#02323F]"
                : "text-gray-600 hover:text-[#02323F]"
            }`}
          >
            {tab.label}
            <span
              className={`absolute left-0 right-0 -bottom-1 h-1 bg-[#02323F] rounded-full transition-all duration-300 ${
                activeTab === tab.key ? "w-full opacity-100" : "w-0 opacity-0"
              }`}
            />
          </button>
        ))}
      </div>

      {/* Upload Controls */}
      <div className="mb-6">
        {/* Single input per tab, but allow multiple selection */}
        {activeTab === "image" && (
          <label className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-500 transition-colors mb-4 bg-gray-50">
            <FiUpload className="mr-2 text-xl text-gray-500" />
            <span>Click to select images</span>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*"
              className="hidden"
              onChange={handleImageChange}
            />
          </label>
        )}

        {activeTab === "video" && (
          <label className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-500 transition-colors mb-4 bg-gray-50">
            <FiUpload className="mr-2 text-xl text-gray-500" />
            <span>Click to select videos (max 25MB each)</span>
            <input
              ref={videoInputRef}
              type="file"
              multiple
              accept="video/*"
              className="hidden"
              onChange={handleVideoChange}
            />
          </label>
        )}

        {activeTab === "media" && (
          <label className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-500 transition-colors mb-4 bg-gray-50">
            <FiUpload className="mr-2 text-xl text-gray-500" />
            <span>Click to select media publications </span>
            <input
              ref={mediaInputRef}
              type="file"
              multiple
              accept="image/*"
              className="hidden"
              onChange={handleMediaChange}
            />
          </label>
        )}
      </div>

      {/* Preview Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-6">
        {items.map((it, idx) => (
          <div key={it.id} className="relative group">
            <div className="w-full h-48 object-cover rounded-lg shadow-md overflow-hidden bg-gray-50">
              {it.category === "video" ? (
                // Video preview is playable now (controls enabled)
                <video
                  src={it.preview}
                  controls
                  muted={false}
                  playsInline
                  className="w-full h-full object-cover"
                />
              ) : (
                <img
                  src={it.preview}
                  alt={it.title || "preview"}
                  className="w-full h-full object-cover"
                />
              )}
            </div>

            {/* Uploading overlay */}
            {it.uploading && (
              <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-60 rounded-lg">
                <div className="w-8 h-8 border-4 border-blue-400 border-dashed rounded-full animate-spin" />
              </div>
            )}

            {/* Remove button top-right */}
            <button
              onClick={() => handleRemoveItem(it.id)}
              className="absolute top-2 right-2 bg-red-500 p-1 rounded-full text-white opacity-0 group-hover:opacity-100 transition-opacity"
              title="Remove"
            >
              <FiX />
            </button>

            {/* Per-item fields: title & sequence */}
            <div className="mt-2 space-y-2">
              <input
                type="text"
                value={it.title}
                placeholder="Title (optional)"
                onChange={(e) =>
                  handleItemFieldChange(it.id, "title", e.target.value)
                }
                className="w-full px-2 py-1 border rounded-md focus:outline-none placeholder:text-xs text-sm"
              />
              <div className="flex items-center justify-between gap-2">
                <label className="text-xs pl-2 text-gray-600 whitespace-nowrap">
                  Sequence(Optional)
                </label>
                <input
                  type="number"
                  min={0}
                  value={it.sequence}
                  onChange={(e) =>
                    handleItemFieldChange(it.id, "sequence", e.target.value)
                  }
                  className="w-20 px-2 py-1 border rounded-md focus:outline-none text-sm"
                  placeholder="0"
                />
              </div>

              {/* Show uploaded URL if exists */}
              {it.url && (
                <div className="text-xs pl-2 text-green-600 break-words">
                  Uploaded
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Floating actions */}
      <div className="fixed bottom-6 right-6 flex items-center gap-3 z-50">
        <button
          onClick={handleUploadAll}
          disabled={uploadingAll || items.length === 0}
          className={`flex items-center justify-center px-5 py-3 bg-blue-500 text-white rounded-full shadow-lg hover:bg-blue-600 transition-colors ${
            uploadingAll || items.length === 0
              ? "opacity-50 cursor-not-allowed"
              : ""
          }`}
        >
          <FiUpload className="mr-2" />
          {uploadingAll ? "Uploading..." : "Upload Files"}
        </button>

        <button
          onClick={handleSaveAll}
          disabled={!allUploaded}
          className={`px-5 py-3 bg-green-500 text-white rounded-full shadow-lg hover:bg-green-600 transition-colors ${
            !allUploaded ? "opacity-50 cursor-not-allowed" : ""
          }`}
        >
          Save Gallery
        </button>
      </div>
    </div>
  );
};

export default AdminUploadGallery;

// import React, { useState, useRef, useEffect } from "react";
// import { FiUpload, FiX } from "react-icons/fi";
// import { toast } from "react-hot-toast";
// import { v4 as uuidv4 } from "uuid";
// import uploadImage from "../../utils/CloudinaryUpload";
// import { uploadVideoToCloudinary } from "../../utils/cloudinary";
// import axiosInstance from "../../utils/axios";
// import { useNavigate } from "react-router-dom";
// import { FaArrowLeft } from "react-icons/fa";

// const AdminUploadGallery = () => {
//   const [items, setItems] = useState([]); // unified list for image/video/media files { id, file, preview, title, sequence, uploading, url, category }
//   const [uploadingAll, setUploadingAll] = useState(false);
//   const [activeTab, setActiveTab] = useState("image"); // image | video | media
//   const fileInputRef = useRef(null);
//   const videoInputRef = useRef(null);
//   const mediaInputRef = useRef(null);
//   const navigate = useNavigate();

//   // Helpers
//   const resetInputs = () => {
//     if (fileInputRef.current) fileInputRef.current.value = "";
//     if (videoInputRef.current) videoInputRef.current.value = "";
//     if (mediaInputRef.current) mediaInputRef.current.value = "";
//   };

//   // Add selected files to items array (category is set based on tab)
//   const addFiles = (files, category) => {
//     if (!files || files.length === 0) return;
//     const newFiles = Array.from(files).map((file, idx) => ({
//       id: uuidv4(),
//       file,
//       preview: URL.createObjectURL(file),
//       title: "",
//       sequence: items.length + idx + 1, // default sequence appended
//       uploading: false,
//       url: null,
//       category,
//     }));
//     setItems((prev) => [...prev, ...newFiles]);
//     resetInputs();
//   };

//   // Handlers for file inputs
//   const handleImageChange = (e) => {
//     addFiles(e.target.files, "image");
//   };

//   const handleVideoChange = (e) => {
//     addFiles(e.target.files, "video");
//   };

//   const handleMediaChange = (e) => {
//     // media are treated like images for upload
//     addFiles(e.target.files, "media");
//   };

//   // Remove an item
//   const handleRemoveItem = (id) => {
//     const it = items.find((i) => i.id === id);
//     if (it && it.preview) URL.revokeObjectURL(it.preview);
//     setItems((prev) => prev.filter((i) => i.id !== id));
//     toast.success("Removed");
//   };

//   // Update title or sequence per item
//   const handleItemFieldChange = (id, field, value) => {
//     setItems((prev) =>
//       prev.map((it) =>
//         it.id === id
//           ? {
//               ...it,
//               [field]: field === "sequence" ? Number(value || 0) : value,
//             }
//           : it
//       )
//     );
//   };

//   const uploadSingleItem = async (item) => {
//     // Skip if already uploaded (url present)
//     if (item.url) return { ...item };

//     const copy = { ...item, uploading: true };
//     setItems((prev) => prev.map((i) => (i.id === item.id ? copy : i)));

//     try {
//       let uploadedUrl = null;
//       if (item.category === "video") {
//         uploadedUrl = await uploadVideoToCloudinary(item.file);
//       } else {
//         // image or media
//         uploadedUrl = await uploadImage(item.file);
//       }

//       const done = { ...copy, uploading: false, url: uploadedUrl };
//       setItems((prev) => prev.map((i) => (i.id === item.id ? done : i)));
//       return done;
//     } catch (err) {
//       console.error("Upload failed for item:", item, err);
//       const failed = { ...copy, uploading: false };
//       setItems((prev) => prev.map((i) => (i.id === item.id ? failed : i)));
//       throw err;
//     }
//   };

//   // Upload all (only files without url)
//   const handleUploadAll = async () => {
//     if (items.length === 0) {
//       toast.error("No files to upload");
//       return;
//     }

//     setUploadingAll(true);

//     try {
//       const itemsToUpload = items.filter((it) => !it.url);

//       // If everything is already uploaded
//       if (itemsToUpload.length === 0) {
//         toast.success("All files already uploaded");
//         setUploadingAll(false);
//         return;
//       }

//       // Run uploads in parallel
//       await Promise.all(itemsToUpload.map((item) => uploadSingleItem(item)));

//       toast.success("All files uploaded successfully!");
//     } catch (err) {
//       console.error("Upload error:", err);
//       toast.error("Some uploads failed. Please retry.");
//     } finally {
//       setUploadingAll(false);
//     }
//   };

//   // Save all to backend (bulk)
//   const handleSaveAll = async () => {
//     // prepare payload only for uploaded files
//     const payloadItems = items
//       .filter((it) => it.url) // ensure uploaded
//       .map((it) => ({
//         title: it.title || "",
//         category: it.category,
//         fileUrl: it.url,
//         sequence: typeof it.sequence === "number" ? it.sequence : 0,
//       }));

//     if (payloadItems.length === 0) {
//       toast.error("No uploaded files to save. Upload first.");
//       return;
//     }

//     try {
//       await axiosInstance.post("/gallery/bulk", { items: payloadItems });
//       toast.success("Gallery saved successfully");
//       // cleanup previews
//       items.forEach((it) => it.preview && URL.revokeObjectURL(it.preview));
//       setItems([]);
//     } catch (err) {
//       console.error("Save bulk failed", err);
//       toast.error("Save failed");
//     }
//   };

//   // Determine if all items are uploaded
//   const allUploaded = items.length > 0 && items.every((it) => it.url);

//   // UI: keep design identical; only add fields under each preview: title, sequence
//   return (
//     <div className="p-6 max-w-full mx-auto relative min-h-screen">
//       {/* Header */}
//       <div className="w-full flex justify-between items-center mb-8">
//         <button
//           onClick={() => navigate(-1)}
//           className="flex items-center justify-center w-12 h-12 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors duration-200 group"
//         >
//           <FaArrowLeft className="text-gray-600 group-hover:text-gray-800 transition-colors duration-200" />
//         </button>
//         <div className="grow flex items-center justify-start px-4">
//           <h1 className="text-3xl text-left font-bold">
//             {activeTab === "video"
//               ? "Upload Videos"
//               : activeTab === "media"
//               ? "Upload Media Files"
//               : "Upload one or multiple images"}
//           </h1>
//         </div>
//         <div>
//           {items.length > 0 && (
//             <div className="flex items-center gap-2">
//               <button
//                 onClick={() => {
//                   setItems([]);
//                   resetInputs();
//                 }}
//                 className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white font-semibold rounded-lg shadow-md transition-all duration-200"
//               >
//                 Clear
//               </button>
//             </div>
//           )}
//         </div>
//       </div>

//       {/* Tabs */}
//       <div className="flex justify-start mt-8 mb-6 border-b border-gray-300">
//         {[
//           { key: "image", label: "Images" },
//           { key: "video", label: "Videos" },
//           { key: "media", label: "Media Files" },
//         ].map((tab) => (
//           <button
//             key={tab.key}
//             onClick={() => setActiveTab(tab.key)}
//             className={`relative px-6 py-3 mx-2 text-sm md:text-base font-semibold transition-all duration-300 ${
//               activeTab === tab.key
//                 ? "text-[#02323F]"
//                 : "text-gray-600 hover:text-[#02323F]"
//             }`}
//           >
//             {tab.label}
//             <span
//               className={`absolute left-0 right-0 -bottom-1 h-1 bg-[#02323F] rounded-full transition-all duration-300 ${
//                 activeTab === tab.key ? "w-full opacity-100" : "w-0 opacity-0"
//               }`}
//             />
//           </button>
//         ))}
//       </div>

//       {/* Upload Controls */}
//       <div className="mb-6">
//         {/* Single input per tab, but allow multiple selection */}
//         {activeTab === "image" && (
//           <label className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-500 transition-colors mb-4 bg-gray-50">
//             <FiUpload className="mr-2 text-xl text-gray-500" />
//             <span>Click to select images</span>
//             <input
//               ref={fileInputRef}
//               type="file"
//               multiple
//               accept="image/*"
//               className="hidden"
//               onChange={handleImageChange}
//             />
//           </label>
//         )}

//         {activeTab === "video" && (
//           <label className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-500 transition-colors mb-4 bg-gray-50">
//             <FiUpload className="mr-2 text-xl text-gray-500" />
//             <span>Click to select videos (max 25MB each)</span>
//             <input
//               ref={videoInputRef}
//               type="file"
//               multiple
//               accept="video/*"
//               className="hidden"
//               onChange={handleVideoChange}
//             />
//           </label>
//         )}

//         {activeTab === "media" && (
//           <label className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-500 transition-colors mb-4 bg-gray-50">
//             <FiUpload className="mr-2 text-xl text-gray-500" />
//             <span>
//               Click to select media files
//             </span>
//             <input
//               ref={mediaInputRef}
//               type="file"
//               multiple
//               accept="image/*"
//               className="hidden"
//               onChange={handleMediaChange}
//             />
//           </label>
//         )}
//       </div>

//       {/* Preview Grid */}
//       <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-6">
//         {items.map((it, idx) => (
//           <div key={it.id} className="relative group">
//             <div className="w-full h-48 object-cover rounded-lg shadow-md overflow-hidden bg-gray-50">
//               {it.category === "video" ? (
//                 <video
//                   src={it.preview}
//                   controls={false}
//                   muted
//                   playsInline
//                   className="w-full h-full object-cover"
//                 />
//               ) : (
//                 <img
//                   src={it.preview}
//                   alt={it.title || "preview"}
//                   className="w-full h-full object-cover"
//                 />
//               )}
//             </div>

//             {/* Uploading overlay */}
//             {it.uploading && (
//               <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-60 rounded-lg">
//                 <div className="w-8 h-8 border-4 border-blue-400 border-dashed rounded-full animate-spin" />
//               </div>
//             )}

//             {/* Remove button top-right */}
//             <button
//               onClick={() => handleRemoveItem(it.id)}
//               className="absolute top-2 right-2 bg-red-500 p-1 rounded-full text-white opacity-0 group-hover:opacity-100 transition-opacity"
//               title="Remove"
//             >
//               <FiX />
//             </button>

//             {/* Per-item fields: title & sequence */}
//             <div className="mt-2 space-y-2">
//               <input
//                 type="text"
//                 value={it.title}
//                 placeholder="Title (optional)"
//                 onChange={(e) =>
//                   handleItemFieldChange(it.id, "title", e.target.value)
//                 }
//                 className="w-full px-2 py-1 border rounded-md focus:outline-none placeholder:text-xs text-sm"
//               />
//               <div className="flex items-center justify-between gap-2">
//                 <label className="text-xs pl-2 text-gray-600 whitespace-nowrap">
//                   Sequence(Optional)
//                 </label>
//                 <input
//                   type="number"
//                   min={0}
//                   value={it.sequence}
//                   onChange={(e) =>
//                     handleItemFieldChange(it.id, "sequence", e.target.value)
//                   }
//                   className="w-20 px-2 py-1 border rounded-md focus:outline-none text-sm"
//                   placeholder="0"
//                 />
//               </div>

//               {/* Show uploaded URL if exists */}
//               {it.url && (
//                 <div className="text-xs pl-2 text-green-600 break-words">
//                   Uploaded
//                 </div>
//               )}
//             </div>
//           </div>
//         ))}
//       </div>

//       {/* Floating actions */}
//       <div className="fixed bottom-6 right-6 flex items-center gap-3 z-50">
//         <button
//           onClick={handleUploadAll}
//           disabled={uploadingAll || items.length === 0}
//           className={`flex items-center justify-center px-5 py-3 bg-blue-500 text-white rounded-full shadow-lg hover:bg-blue-600 transition-colors ${
//             uploadingAll || items.length === 0
//               ? "opacity-50 cursor-not-allowed"
//               : ""
//           }`}
//         >
//           <FiUpload className="mr-2" />
//           {uploadingAll ? "Uploading..." : "Upload Files"}
//         </button>

//         <button
//           onClick={handleSaveAll}
//           disabled={!allUploaded}
//           className={`px-5 py-3 bg-green-500 text-white rounded-full shadow-lg hover:bg-green-600 transition-colors ${
//             !allUploaded ? "opacity-50 cursor-not-allowed" : ""
//           }`}
//         >
//           Save Gallery
//         </button>
//       </div>
//     </div>
//   );
// };

// export default AdminUploadGallery;

// import React, { useState, useRef } from "react";
// import { FiUpload, FiX } from "react-icons/fi";
// import { toast } from "react-hot-toast";
// import { v4 as uuidv4 } from "uuid";
// import uploadImage from "../../utils/CloudinaryUpload";
// import axiosInstance from "../../utils/axios";
// import { useNavigate } from "react-router-dom";
// import { FaArrowLeft } from "react-icons/fa";

// const AdminUploadGallery = () => {
//   const [images, setImages] = useState([]); // {id, file, preview, title, uploading, url}
//   const [uploadingAll, setUploadingAll] = useState(false);
//   const [activeTab, setActiveTab] = useState("image");
//   const fileInputRef = useRef(null);
//   const navigate = useNavigate();

//   // Handle file selection
//   const handleFileChange = (e) => {
//     const files = Array.from(e.target.files);
//     if (!files.length) return;

//     e.target.value = ""; // reset input to allow re-selecting same images

//     const newImages = files.map((file) => ({
//       id: uuidv4(),
//       file,
//       preview: URL.createObjectURL(file),
//       title: "",
//       uploading: false,
//       url: null,
//     }));

//     setImages((prev) => [...prev, ...newImages]);
//   };

//   // Remove image
//   const handleRemoveImage = (id) => {
//     const imgToRemove = images.find((img) => img.id === id);
//     if (imgToRemove && imgToRemove.preview)
//       URL.revokeObjectURL(imgToRemove.preview);

//     setImages((prev) => prev.filter((img) => img.id !== id));
//     toast.success("Image removed!");
//   };

//   // Update image title
//   const handleTitleChange = (id, value) => {
//     setImages((prev) =>
//       prev.map((img) => (img.id === id ? { ...img, title: value } : img))
//     );
//   };
//   const handleRemoveAll = () => {
//     setImages([]);
//   };
//   // Upload all images to Cloudinary
//   const handleUploadAll = async () => {
//     setUploadingAll(true);
//     const updatedImages = [...images];

//     for (let i = 0; i < updatedImages.length; i++) {
//       if (updatedImages[i].url) continue; // skip already uploaded
//       updatedImages[i].uploading = true;
//       setImages([...updatedImages]);

//       try {
//         const url = await uploadImage(updatedImages[i].file);
//         updatedImages[i].url = url;
//       } catch (err) {
//         console.error(err);
//       } finally {
//         updatedImages[i].uploading = false;
//         setImages([...updatedImages]);
//       }
//     }

//     setUploadingAll(false);
//     toast.success("All images uploaded! You can now save the gallery.");
//   };

//   // Save all images to backend
//   const handleSaveAll = async () => {
//     try {
//       const payload = images.map((img) => ({
//         title: img.title,
//         image: img.url,
//       }));
//       await axiosInstance.post(`/gallery/bulk`, { items: payload });
//       toast.success("Gallery saved successfully!");
//       setImages([]);
//     } catch (err) {
//       toast.error("Failed to save gallery!");
//       console.error(err);
//     }
//   };

//   // Determine if all images are uploaded
//   const allUploaded = images.length > 0 && images.every((img) => img.url);

//   const tabs = [
//     { key: "image", label: "Images" },
//     { key: "video", label: "Videos" },
//     { key: "media", label: "Media Files" },
//   ];

//   return (
//     <div className="p-6 max-w-full mx-auto relative min-h-screen">
//       {/* Heading */}
//       <div className="w-full flex justify-between items-center mb-8">
//         <button
//           onClick={() => navigate(-1)}
//           className="flex items-center  justify-center w-12 h-12 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors duration-200 group"
//         >
//           <FaArrowLeft className="text-gray-600 group-hover:text-gray-800 transition-colors duration-200" />
//         </button>
//         <div className="grow flex items-center justify-start px-4">
//           <h1 className="text-3xl text-left font-bold">
//             Upload one or multiple images
//           </h1>
//         </div>
//         <div className="">
//           {images.length > 1 && (
//             <button
//               onClick={handleRemoveAll}
//               className="flex items-center px-4 py-2 bg-red-500 hover:bg-red-600 text-white font-semibold rounded-lg shadow-md transition-all duration-200 hover:scale-105 active:scale-95"
//             >
//               <FiX className="mr-2" />
//               Remove All
//             </button>
//           )}
//         </div>
//       </div>

//       {/* Tab Section */}
//       <div className="flex justify-start mt-8 mb-6 border-b border-gray-300">
//         {tabs.map((tab) => (
//           <button
//             key={tab.key}
//             onClick={() => setActiveTab(tab.key)}
//             className={`relative px-6 py-3 mx-2 text-sm md:text-base font-semibold transition-all duration-300
//         ${
//           activeTab === tab.key
//             ? "text-[#02323F]"
//             : "text-gray-600 hover:text-[#02323F]"
//         }`}
//           >
//             {tab.label}

//             <span
//               className={`absolute left-0 right-0 -bottom-1 h-1 bg-[#02323F] rounded-full transition-all duration-300
//           ${activeTab === tab.key ? "w-full opacity-100" : "w-0 opacity-0"}`}
//             ></span>
//           </button>
//         ))}
//       </div>

//       {/* Upload button */}
//       <label className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-500 transition-colors mb-6 bg-gray-50">
//         <FiUpload className="mr-2 text-xl text-gray-500" />
//         <span>
//           {images ? "Click to upload more images" : "Click to upload images"}
//         </span>
//         <input
//           type="file"
//           multiple
//           accept="image/*"
//           className="hidden"
//           onChange={handleFileChange}
//         />
//       </label>

//       {/* Image preview grid */}
//       <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-6">
//         {images.map((img) => (
//           <div key={img.id} className="relative group">
//             <img
//               src={img.url || img.preview}
//               alt="Uploaded"
//               className={`w-full h-48 object-cover rounded-lg shadow-md ${
//                 img.uploading ? "opacity-50" : ""
//               }`}
//             />
//             {/* Spinner */}
//             {img.uploading && (
//               <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-40 rounded-lg">
//                 <div className="w-8 h-8 border-4 border-blue-400 border-dashed rounded-full animate-spin"></div>
//               </div>
//             )}

//             {/* Remove button */}
//             <button
//               onClick={() => handleRemoveImage(img.id)}
//               className="absolute top-2 right-2 bg-red-500 p-1 rounded-full text-white opacity-0 group-hover:opacity-100 transition-opacity"
//             >
//               <FiX />
//             </button>

//             {/* Optional title input */}
//             <input
//               type="text"
//               placeholder="Title...(Optional)"
//               value={img.title}
//               onChange={(e) => handleTitleChange(img.id, e.target.value)}
//               className="mt-2 w-full px-2 py-1 border rounded-md focus:outline-none placeholder:text-xs focus:ring-1 focus:ring-blue-400 text-sm"
//             />
//           </div>
//         ))}
//       </div>

//       {/* Floating Upload/Save Button */}
//       <div className="fixed bottom-6 right-6">
//         {allUploaded ? (
//           <button
//             onClick={handleSaveAll}
//             className="px-6 py-3 bg-green-500 text-white rounded-full shadow-lg hover:bg-green-600 transition-colors"
//           >
//             Save Gallery
//           </button>
//         ) : (
//           <button
//             onClick={handleUploadAll}
//             disabled={uploadingAll || images.length === 0}
//             className={`flex items-center justify-center px-6 py-3 bg-blue-500 text-white rounded-full shadow-lg hover:bg-blue-600 transition-colors ${
//               uploadingAll || images.length === 0
//                 ? "opacity-50 cursor-not-allowed"
//                 : ""
//             }`}
//           >
//             <FiUpload className="mr-2 text-xl" />
//             {uploadingAll ? "Uploading..." : "Upload Images"}
//           </button>
//         )}
//       </div>
//     </div>
//   );
// };

// export default AdminUploadGallery;
